<template>
    <div class="group-rounded-box">
        <span class="title">Global Product Rules</span>

        <div class="row">
            <!-- Target attributes (Density, Duty, Bio Blend / RTFO%) -->
            <div class="col-lg-4 col-md-6 mb-3" 
                v-for="(att, key) in targetAttributes" :key="key">
                <label>{{ att.name }}</label>

                <!-- Bio Blend / RTFO% -->
                <div v-if="att.id === rulesEnum.eTargetAttributeType.BioBlend">
                    <!-- Bio-element% -->
                    <div class="form-group" :class="{'d-flex align-items-center justify-content-between': !$breakpoint.mdAndDown}">
                        <label class="font-weight-normal" :class="[{'d-block mb-1': $breakpoint.mdAndDown}, {'text-t_red font-weight-bold': errorIds.includes(eRtfoId.Bio)}]">Bio-element</label>
                        <cleave 
                            :options="numeralPositive" 
                            class="form-control text-center" 
                            :class="[$breakpoint.mdAndDown? 'w-100' : 'w-50', {'border-t_red': errorIds.includes(eRtfoId.Bio)}]" 
                            v-model="rules.find(r => r.action.targetAttribute === rulesEnum.eTargetAttributeType.BioBlend).action.value" 
                            @keyup.native="calculateRtfoPercentage(eRtfoId.Diesel, false, $event)" 
                            @blur="validateRtfoInput()"
                        ></cleave>
                    </div>
                    
                    <!-- Diesel% -->
                    <div class="form-group" :class="{'d-flex align-items-center justify-content-between': !$breakpoint.mdAndDown}">
                        <label class="font-weight-normal" :class="[{'d-block mb-1': $breakpoint.mdAndDown}, {'text-t_red font-weight-bold': errorIds.includes(eRtfoId.Diesel)}]">{{ products.find(p => p._id === productConfig.DieselId).name }}</label>
                        <cleave 
                            :options="numeralPositive" 
                            class="form-control text-center" 
                            :class="[$breakpoint.mdAndDown? 'w-100' : 'w-50', {'border-t_red': errorIds.includes(eRtfoId.Diesel)}]"
                            v-model="dieselPercentage" 
                            @keyup.native="calculateRtfoPercentage(eRtfoId.Bio, false, $event)" 
                            @blur="validateRtfoInput()"
                        ></cleave>
                    </div>
                </div>

                <!-- Product density or duty -->
                <div v-else v-for="(product, idx) in products" :key="idx" class="form-group" :class="{'d-flex align-items-center justify-content-between': !$breakpoint.mdAndDown}">
                    <!-- No need to display input field for Biodiesel's Duty -->
                    <template v-if="!(product._id === productConfig.BioDieselId && att.id === rulesEnum.eTargetAttributeType.Duty)"> 
                        <label class="font-weight-normal" :class="$breakpoint.mdAndDown ? 'd-block mb-1' : 'w-50'">{{ product.name }}</label>
                        <cleave :options="numeralPositive" class="form-control text-center" :class="$breakpoint.mdAndDown? 'd-inline-block w-75' : 'w-50'" 
                            v-model="rules.find(r => 
                                        r.targets[0] && r.targets[0].type === rulesEnum.eTargetType.Product && r.targets[0].ids.includes(product._id) // Match with rule that target the current product
                                        && r.action.targetAttribute === att.id // Match with rule that targets the current target attribute
                                    ).action.value" 
                            @blur="validateInput(att.id, product._id)"
                        ></cleave>
                        <span class="d-inline-block w-25 text-center">{{ att.unitOfMeasurement.display }}</span>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import breakpoint from '@/mixins/index';
import { mapGetters } from "vuex";

import { rulesEnum } from "@/util/enums/rules";


export default {
    mixins: [breakpoint],
    props: {
        rules: {
            type: Array,
            required: true
        },
        products: {
            type: Array,
            required: true
        },
        componentKey: {
            type: String,
            default: "PRODUCT"
        }
    },
    data(){
        return {
            dieselPercentage: 0,
            eRtfoId: {
                Bio: "BIO",
                Diesel: "DIESEL"
            },
            isDataLoaded: false,
            errorIds: [],
            rulesEnum
        }
    },
    mounted(){
        this.calculateRtfoPercentage(this.eRtfoId.Diesel, true); // Trigger the calculation to happen to work out Diesel%
        this.isDataLoaded = true;
    },
    computed: {
        ...mapGetters('inputCheckers', ['numeralPositive']),
        ...mapGetters('metadata', ['productConfig']),
        targetAttributes: {
            get: function(){
                return [
                    { name: "Density", id: rulesEnum.eTargetAttributeType.Density, unitOfMeasurement: rulesEnum.eUnitType.LitresPerMetricTonne },
                    { name: "Duty", id: rulesEnum.eTargetAttributeType.Duty, unitOfMeasurement: rulesEnum.eUnitType.PencePerLitre },
                    { name: "Bio Blend / RTFO%", id: rulesEnum.eTargetAttributeType.BioBlend }
                ];
            }
        },
        trackError: function(){ // Flag to the parent component whether there is error found or not
            let status = this.errorIds.length > 0;
            const MAX_VALUE = 100;

            if (status == false){
                const bioElementPercentage = this.rules.find(r => r.action.targetAttribute === rulesEnum.eTargetAttributeType.BioBlend).action.value;
                status = bioElementPercentage > MAX_VALUE || this.dieselPercentage > MAX_VALUE;
            }

            this.$emit("errorFound", this.componentKey, status);
        }
    },
    watch: {
        trackError(){}
    },
    methods: {
        calculateRtfoPercentage: function(rtfoIdToCalculate, isCalculationTriggered, event){
            if (event && event.keyCode == 9) return; // If user presses TAB, then no need to do anything (onblur is handled separately)

            if (this.isDataLoaded || isCalculationTriggered){ // Only perform calculation when all data has been loaded or when it is triggered specifically
                if (rtfoIdToCalculate === this.eRtfoId.Diesel){
                    // Formula: Diesel% = 100% - Bio-element%
                    const bioElementPercentage = this.rules.find(r => r.action.targetAttribute === rulesEnum.eTargetAttributeType.BioBlend).action.value;
                    const result = 100 - bioElementPercentage;
                    this.dieselPercentage = result < 0 ? 0 : Number(result).toFixed(2); // We cannot have values above 100% anyway, so let's default it to 0
                } else {
                    // Formula: Bio-element% = 100% - Diesel%
                    const result = 100 - this.dieselPercentage;
                    this.rules.find(r => r.action.targetAttribute === rulesEnum.eTargetAttributeType.BioBlend).action.value = result < 0 ? 0 : Number(result).toFixed(2); // We cannot have values above 100% anyway, so let's default it to 0
                }
            }
        },
        validateInput: function(targetAttribute, productId){
            let value = 0;
            let isValueInvalid = false;
            const inputId = Object.values(this.eRtfoId).includes(targetAttribute) ? targetAttribute : productId;
            const errorIdIndex = this.errorIds.findIndex(id => id === inputId);

            switch(targetAttribute){
                case this.eRtfoId.Bio:
                    // Assign value and set it to 2 decimal places and check if value is valid or not
                    value = this.rules.find(r => r.action.targetAttribute === rulesEnum.eTargetAttributeType.BioBlend).action.value;
                    this.rules.find(r => r.action.targetAttribute === rulesEnum.eTargetAttributeType.BioBlend).action.value = Number(value).toFixed(2);
                    isValueInvalid = value > 100;
                    break;
                case this.eRtfoId.Diesel:
                    // Assign value and set it to 2 decimal places and check if value is valid or not
                    value = this.dieselPercentage;
                    this.dieselPercentage = Number(value).toFixed(2);
                    isValueInvalid = value > 100;
                    break;
                default:
                    // Assign value and set it to 2 decimal places
                    value = this.rules.find(r => 
                                r.targets[0] && r.targets[0].type === rulesEnum.eTargetType.Product && r.targets[0].ids.includes(productId) // Match with rule that target the current product
                                && r.action.targetAttribute === targetAttribute // Match with rule that targets the current target attribute
                            ).action.value;
                    this.rules.find(r => 
                        r.targets[0] && r.targets[0].type === rulesEnum.eTargetType.Product && r.targets[0].ids.includes(productId) // Match with rule that target the current product
                        && r.action.targetAttribute === targetAttribute // Match with rule that targets the current target attribute
                    ).action.value = Number(value).toFixed(2);
                    break;
            }
            
            if (isValueInvalid){
                if (errorIdIndex == -1) // If this error ID has not existed already, then add it to the list of errors
                    this.errorIds.push(inputId);
            } else {
                if (errorIdIndex > -1) // If this error ID exists, then we can remove it from the list
                    this.errorIds.splice(errorIdIndex, 1);
            }
        },
        validateRtfoInput(){ // If we are validating either of the RTFO inputs, make sure we check both straightaway to ensure total does not exceed 100%
            this.validateInput(this.eRtfoId.Bio);
            this.validateInput(this.eRtfoId.Diesel);
        }
    }
}
</script>

<style lang="scss" scoped>

</style>